import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import * as models from 'smg/admin/models';
import { GetRealm } from 'smg/admin/realms';
import axios from 'smg/axios';
import * as cc from 'smg/cc';
import * as countries from 'smg/countries';
import { ShowError } from 'smg/errors';
import { Phone } from 'smg/phone';
import * as blockchyp from 'smg/blockchyp';
import * as stax from 'smg/stax';
import * as template from 'smg/template';
import * as uploader from 'smg/uploader/v2';
import { DollarFormat } from 'smg/util';
import { RenderUsageMeta } from './cardusage';
import { ChargeOrderPayment } from './orderpayments';
import i18n from '../i18n';

export interface PaymentHandle {
    refresh(userID: string): void;
    // Realm changed (or first landed) — the BlockChyp tokenizing key is
    // realm-scoped, so every card mount belongs to the old merchant and must
    // be recycled under the new realm's key. This is also what brings the
    // card iframes up on a page that loads with an existing cart: the
    // initial mounts are attempted before the deferred applyRealm lands (no
    // realm → no mount), and contact's onRealmChange is the first signal
    // that a companyID is actually readable.
    refreshRealm(): void;
    // Mirrors shipping.setShippingDestination on the tax module — when the
    // same-as-shipping checkbox is on, the billing payload inherits this. We
    // never auto-fill the visible form fields though; the user pressing
    // "Override" should see them blank so they don't get a stale shipping
    // copy and miss a needed edit.
    setShippingDestination(addrInit: models.IAddress | undefined): void;
    // Re-evaluate the split-payment warning. Card amounts are compared
    // against summary.getTotal(), which can move when shipping/tax change
    // without any payment input event — so the page wires summary's
    // onTotalChange to this.
    refreshWarning(): void;
    // Freeze every credit-card block's BlockChyp iframes — cross-origin, so a
    // native fieldset `disabled` can't reach them. The place handler calls
    // this at submit start so the admin can't edit a card while the charge
    // sequence is mid-flight; thawIframes reverses it on a pre-charge failure.
    freezeIframes(): void;
    thawIframes(): void;
    // Charge the selected payment method against an already-created order:
    // credit cards one at a time (with the express-style decline→fix→resume
    // flow), a single manual (PO/check/wire) charge, or nothing when payment
    // is skipped. Drives the per-card charge UI and reports button-level state
    // to the caller via deps. Payments happen AFTER the order exists, so the
    // place handler invokes this once it has the orderID.
    chargePayment(deps: ChargePaymentDeps): void;
    // PO numbers the place handler should check for duplicates. Empty when
    // payment is skipped, or when the selected type has no PO field filled in.
    getPurchaseOrderNumbers(): string[];
}

// PaymentDraft is the shape the page wires to order-create. Per-type
// slots are filled in by the matching per-type tasks. For CC the per-
// block source (new vs. saved card) and the visible exp/amount are
// reported here; the actual card tokens are produced at charge time by
// chargePayment (payments happen after the order exists), not here.
export interface PaymentDraft {
    paymentOptionTypeID: models.PaymentOptionTypeID;
    sameAsShipping: boolean;
    billing?: models.Address;
    creditSplits?: CreditDraftSplit[];
    // Purchase-order details. Set when paymentOptionTypeID === PO; the
    // page submitter forwards these onto POST /order-payments as
    // `purchaseOrderNumber` / `purchaseOrderUploadIDs`. uploadIDs is
    // empty until at least one upload has finished — ReportValidity on the
    // form blocks submit while the PO upload proxy is invalid.
    po?: { number: string; uploadIDs: number[] };
}

// Visible state for a single CC block. Either a saved card on file is
// picked (existingPaymentOptionID set) or it's a new-card block (exp
// fields, tokenized at submit). amount is undefined when the admin
// hasn't filled it in yet — for card-1 that defaults to the full order
// total, for additional blocks it's required at tokenize time.
export interface CreditDraftSplit {
    existingPaymentOptionID?: string;
    expMonth?: number;
    expYear?: number;
    amount?: InstanceType<typeof Big>;
}

// A soft decline (or a recoverable charge error) on a credit-card block. The
// place handler flips its button into "Retry charge" mode using this.
export interface PaymentDecline {
    // Provider reason, or a generic message for a thrown charge error — shown
    // in the decline alert.
    message: string;
    // Re-charge every not-yet-charged card. Cards that already charged this
    // run are skipped, so the admin can edit the declined card, add a card to
    // split the remainder, or remove an unprocessed one before retrying.
    retry: () => void;
}

// What chargePayment needs from the place handler, and how it reports back.
// Payment owns the per-card charge UI; place owns the button + decline alert.
export interface ChargePaymentDeps {
    orderID: string;
    // Company the order was placed under — used to fetch the realm's Stax
    // web-payments token for the client-side BlockChyp→Stax exchange.
    companyID: string;
    // Every charge for the selected method completed (or there was nothing to
    // charge — skip). The place handler redirects to the order.
    onComplete: () => void;
    // A soft decline (or recoverable charge error) on a credit-card block.
    onDecline: (decline: PaymentDecline) => void;
    // A hard, non-card failure (manual PO/check/wire charge, or split-amount
    // resolution). The place handler surfaces it and offers a plain retry.
    onError: (err: unknown) => void;
}

export interface PaymentDeps {
    onPaymentChange?: (draft: PaymentDraft | undefined) => void;
    // The "as-if-submitted" total that will be charged across all credit
    // splits — explicit amounts as typed, with card-1's "blank means fill
    // the rest" rule resolved. undefined when payment is skipped or the
    // type isn't CC (PO/check/wire aren't immediate charges). Page wires
    // this to summary.setPaid for the live paid/remaining display.
    onPaidChange?: (paid: InstanceType<typeof Big> | undefined) => void;
    // Realm/company for any per-type uploads (PO docs in task #11). Lives
    // on the contact section so the page wires it through.
    getCompanyID?: () => string | undefined;
    // Returns the order's currently displayed grand total — used to
    // validate that split amounts sum to the right number. Optional: when
    // not given, split validation is skipped and the caller is expected
    // to validate at submit.
    getOrderTotal?: () => InstanceType<typeof Big>;
}

export function SetupPayment(deps?: PaymentDeps): PaymentHandle {
    const $skip = document.getElementById('payment-skip') as HTMLInputElement;
    const $collapse = document.getElementById('payment-form-collapse') as HTMLElement;
    const $fieldset = document.getElementById('payment-fieldset') as HTMLFieldSetElement;
    const $type = document.getElementById('payment-type') as HTMLSelectElement;
    const $clearBtn = document.getElementById('payment-clear') as HTMLButtonElement;

    const $optionsRow = document.getElementById('payment-options-row') as HTMLElement;
    const $optionsScroller = document.getElementById('payment-options-scroller') as HTMLElement;

    const $sameAsShipping = document.getElementById('payment-same-as-shipping') as HTMLInputElement;
    const $billingFieldset = document.getElementById('payment-billing-fieldset') as HTMLFieldSetElement;
    const $billingName = document.getElementById('payment-billing-name') as HTMLInputElement;
    const $billingCompany = document.getElementById('payment-billing-company') as HTMLInputElement;
    const $billingCountry = document.getElementById('payment-billing-country') as HTMLSelectElement;
    const $billingState = document.getElementById('payment-billing-state') as HTMLSelectElement;
    const $billingAddress1 = document.getElementById('payment-billing-address1') as HTMLInputElement;
    const $billingAddress2 = document.getElementById('payment-billing-address2') as HTMLInputElement;
    const $billingCity = document.getElementById('payment-billing-city') as HTMLInputElement;
    const $billingZip = document.getElementById('payment-billing-zip') as HTMLInputElement;
    // Billing recipient phone/email mirror the shipping form's pair so an
    // overridden billing address can still carry contact info downstream
    // (payment-option creation, AVS, receipts). Phone rides intlTelInput
    // so the persisted value lands in E.164 at submit, matching shipping.
    const $billingPhone = document.getElementById('payment-billing-phone') as HTMLInputElement;
    const $billingEmail = document.getElementById('payment-billing-email') as HTMLInputElement;
    const billingPhone = new Phone($billingPhone);

    // CC and PO are <fieldset> so the type switch can toggle disabled and
    // gate their `required` inputs from form validation. Check/wire have no
    // inputs (just informational blocks), so they stay as <div>.
    const typeFieldsetByType = new Map<models.PaymentOptionTypeID, HTMLElement>([
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT, document.getElementById('payment-fields-credit') as HTMLFieldSetElement],
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO, document.getElementById('payment-fields-po') as HTMLFieldSetElement],
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK, document.getElementById('payment-fields-check') as HTMLElement],
        [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE, document.getElementById('payment-fields-wire') as HTMLElement],
    ]);

    const $expMonth1 = document.getElementById('payment-exp-month-1') as HTMLSelectElement;
    const $expYear1 = document.getElementById('payment-exp-year-1') as HTMLSelectElement;
    const $cardAmt1 = document.getElementById('payment-card-amount-1') as HTMLInputElement;
    const $cardBlock1 = document.getElementById('payment-card-block-1') as HTMLElement;
    const $additionalCardBlocks = document.getElementById('payment-additional-card-blocks') as HTMLElement;
    const $splitAdd = document.getElementById('payment-split-add') as HTMLButtonElement;
    const $splitWarning = document.getElementById('payment-split-warning') as HTMLElement;

    const $poNumber = document.getElementById('payment-po-number') as HTMLInputElement;
    const $cardPoNumber = document.getElementById('payment-card-po-number') as HTMLInputElement;
    const $poUploadDropzone = document.getElementById('payment-po-upload-dropzone') as HTMLElement;
    const $poUploadInput = document.getElementById('payment-po-upload-input') as HTMLInputElement;
    const $poUploadPreviews = document.getElementById('payment-po-upload-previews') as HTMLElement;

    // Drive ReportValidity for the PO upload via setCustomValidity. The file
    // input is visually hidden inside the dropzone label (`.validity-parent`),
    // so when invalid the helper walks up to the label and applies
    // `.is-invalid` to the visible dropzone. Same pattern as the tax-exempt
    // certificate and plus/v2 checkout/tax.ts.
    const poUploadEmptyMessage = 'Attach the PO document.';
    function syncPoUploadValidity() {
        $poUploadInput.setCustomValidity(poUploader.uploads().length > 0 ? '' : poUploadEmptyMessage);
        if (poUploader.uploads().length > 0) {
            $poUploadInput.dispatchEvent(new Event('clearinvalid'));
        }
    }

    // Per-block BlockChyp mount registry. Key = block id. Block ids are
    // monotonic and never reused, so a Mount captured at construction
    // never sees its element id migrate. A block in saved-card mode has
    // its mount entry removed (cross-origin frame released); flipping
    // back to new-card mode remounts.
    const cardMounts = new Map<string, blockchyp.Mount>();
    // Per-block saved-card pick (paymentOption.id). Cleared on customer
    // switch via refresh().
    const blockExistingID = new Map<string, string>();
    // Block ID → the amount actually charged this run. Charged cards stay
    // locked (frozen + disabled) through a decline/retry while everything not
    // yet charged can be edited, added, or removed. Storing the amount (not
    // just the ID) keeps the split math honest when the admin adds or edits
    // cards after a partial charge — a charged card contributes its real
    // charged amount, never a recomputed "remainder".
    const chargedAmounts = new Map<string, InstanceType<typeof Big>>();
    // Idempotency keys, one per intended charge attempt (card block / manual
    // type), minted on first use and reused across every retry of that attempt
    // so a retry after a lost response replays the recorded charge instead of
    // charging twice (#5656). Deliberately never reset between retry runs.
    const blockIdemKeys = new Map<string, string>();
    const manualIdemKeys = new Map<models.PaymentOptionTypeID, string>();
    // Blocks whose ChargeOrderPayment POST threw — the charge may have
    // committed server-side with the response lost. Such a block must stay
    // non-removable until a retry resolves it (replay or real decline):
    // removing it abandons its idempotency key, and a replacement card would
    // charge fresh on top of the maybe-committed charge (#5656).
    const ambiguousChargeBlocks = new Set<string>();
    // Monotonic id source for additional card blocks. Never decrements
    // on remove. Display position ("Card 2", "Card 3") is derived from
    // DOM order via relabelCardBlocks; ids are stable so a pending
    // BlockChyp Mount never sees its captured element id reused.
    let nextCardBlockId = 1;

    const billingCountriesReady = countries.Selects($billingCountry, $billingState, {
        defaultUnitedStates: true,
        stateParentSelector: '.payment-billing-state-wrapper',
    });
    void billingCountriesReady.catch(ShowError);

    populateYearSelect($expYear1);

    // PO document uploader. Dropzone is scoped to the visible PO upload
    // label only — not the entire payment card — so dropping a file while
    // CC is selected doesn't silently start a PO upload. companyID for new
    // uploads is resolved at upload-time from the contact section, mirroring
    // the tax-exempt uploader pattern.
    const poUploader = new uploader.Uploader({
        $input: $poUploadInput,
        $dropzones: [$poUploadDropzone],
        $thumbnailsParent: $poUploadPreviews,
        companyID: () => Promise.resolve(deps?.getCompanyID?.() ?? ''),
        onComplete: () => {
            syncPoUploadValidity();
            notifyChange();
        },
        onRemove: () => {
            syncPoUploadValidity();
            notifyChange();
        },
        onRestore: () => {
            syncPoUploadValidity();
            notifyChange();
        },
    });
    // Initial state: no PO upload → setCustomValidity flags the dropzone
    // when the type is PO. The outer fieldset starts disabled (we default to
    // credit) so this doesn't show until the admin switches to PO.
    syncPoUploadValidity();

    let shippingAddr: models.IAddress | undefined;
    let $activeCard: HTMLElement | undefined;
    let options: models.HyejooPaymentOption[] = [];
    let topOptionsLocked = false;

    function selectedType(): models.PaymentOptionTypeID {
        return (parseInt($type.value, 10) || models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) as models.PaymentOptionTypeID;
    }

    function showFieldsetForType(t: models.PaymentOptionTypeID) {
        for (const [type, $el] of typeFieldsetByType) {
            const inactive = type !== t;
            $el.classList.toggle('d-none', inactive);
            // Sub-fieldset disabled keeps the hidden type's `required`
            // inputs out of form validation. Credit and PO are fieldsets;
            // check/wire are divs with no inputs (the cast is a no-op for
            // them).
            if ($el instanceof HTMLFieldSetElement) {
                $el.disabled = inactive;
            }
        }
    }

    function clearActiveCard() {
        $activeCard?.classList.remove('active');
        $activeCard = undefined;
    }

    function currentBillingAddress(): models.Address | undefined {
        if ($sameAsShipping.checked) {
            return shippingAddr ? new models.Address(shippingAddr) : undefined;
        }
        const addressLine1 = $billingAddress1.value.trim();
        const city = $billingCity.value.trim();
        const postalCode = $billingZip.value.trim();
        const stateID = parseInt($billingState.value, 10) || 0;
        const countryID = parseInt($billingCountry.value, 10) || 0;
        if (!addressLine1 || !city || !postalCode || !countryID) {
            return undefined;
        }
        if (countryID === models.CountryID.COUNTRIES_UNITED_STATES && !stateID) {
            return undefined;
        }
        // Phone/email are optional and ride nested submodels on the Address
        // — same shape as the legacy admin addresses-on-file path. Use the
        // raw typed value here; the submit handler awaits billingPhone.format()
        // to land an E.164 string before persisting, mirroring shipping.
        const billingPhoneNumber = $billingPhone.value.trim();
        const billingEmail = $billingEmail.value.trim().toLowerCase();
        return new models.Address({
            fullName: $billingName.value.trim(),
            organization: $billingCompany.value.trim(),
            addressLine1,
            addressLine2: $billingAddress2.value.trim(),
            city,
            stateID,
            postalCode,
            countryID,
            phoneNumber: billingPhoneNumber ? { phone: billingPhoneNumber } : undefined,
            emailAddress: billingEmail ? { email: billingEmail } : undefined,
        });
    }

    function notifyChange() {
        if ($skip.checked) {
            deps?.onPaymentChange?.(undefined);
            deps?.onPaidChange?.(undefined);
            return;
        }
        const type = selectedType();
        const isCC = type === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
        const isPO = type === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO;
        deps?.onPaymentChange?.({
            paymentOptionTypeID: type,
            sameAsShipping: $sameAsShipping.checked,
            billing: currentBillingAddress(),
            creditSplits: isCC ? collectCreditSplits() : undefined,
            po: isPO ? { number: $poNumber.value.trim(), uploadIDs: poUploadIDs() } : undefined,
        });
        deps?.onPaidChange?.(isCC ? computePaid() : undefined);
    }

    // Already-landed PO upload IDs for the draft snapshot. uploads() is
    // synchronous; the draft is informational (page summary), not a submit
    // payload, so we read the IDs that have an assigned id and ignore any
    // in-flight S3 PUTs — the submit path (chargeManual) awaits uploadIDs()
    // to block on those instead.
    function poUploadIDs(): number[] {
        return poUploader.uploads().map(upload => upload.id).filter((id): id is number => Boolean(id));
    }

    // The total that will be charged across all credit splits — drives the
    // "Will charge X of Y" warning. An already-charged block contributes its
    // real charged amount (so a partial charge plus added/edited cards reads
    // honestly), explicit cards their typed value; a blank card-1 fills the
    // remainder up to the order total. Blank additional blocks count as zero
    // (display-time view; tokenize rejects those at submit).
    function computePaid(): InstanceType<typeof Big> {
        const total = deps?.getOrderTotal?.() ?? Big(0);
        const $blocks = getCardBlockElements();
        let fixedSum = Big(0);
        let card1Blank = false;
        for (let i = 0; i < $blocks.length; i++) {
            const charged = chargedAmounts.get($blocks[i].id);
            if (charged !== undefined) {
                fixedSum = fixedSum.plus(charged);
                continue;
            }
            const s = getCardFields($blocks[i]).$amount.value.trim();
            if (i === 0 && !s) {
                card1Blank = true;
                continue;
            }
            const v = parseFloat(s);
            if (!isNaN(v) && v > 0) {
                fixedSum = fixedSum.plus(Big(v));
            }
        }
        if (!card1Blank) {
            return fixedSum;
        }
        // Card-1 blank: absorbs the remainder, but clamped to >= 0 so
        // over-entered other cards don't make the visible "paid" misleadingly
        // small. Tokenize itself rejects this case explicitly.
        const remainder = total.minus(fixedSum);
        return remainder.lt(0) ? fixedSum : total;
    }

    // Per-card view of the visible CC entry. Each entry is one charge —
    // either against a saved card (existingPaymentOptionID set, no token
    // needed) or a new card (exp captured here, token fetched at submit).
    function collectCreditSplits(): CreditDraftSplit[] {
        const splits: CreditDraftSplit[] = [];
        for (const $block of getCardBlockElements()) {
            const fields = getCardFields($block);
            const amountStr = fields.$amount.value.trim();
            const amount = amountStr ? Big(amountStr) : undefined;
            const existing = blockExistingID.get($block.id);
            if (existing) {
                splits.push({ existingPaymentOptionID: existing, amount });
                continue;
            }
            const expMonth = parseInt(fields.$expMonth.value, 10);
            const expYear = parseInt(fields.$expYear.value, 10);
            splits.push({ expMonth, expYear, amount });
        }
        return splits;
    }

    function mountCardBlock($blockEl: HTMLElement) {
        const blockId = $blockEl.id;
        const existing = cardMounts.get(blockId);
        if (existing && !existing.isUnloaded) {
            return;
        }
        if (existing) {
            // Retired mount (a tokenize timed out, which removes its frames —
            // see ts/blockchyp.ts). Rebuild so the admin has live fields.
            cardMounts.delete(blockId);
        }
        const $number = $blockEl.querySelector<HTMLElement>('.card-iframe-mount.payment-card-number-input') ??
            (blockId === 'payment-card-block-1' ? document.getElementById('payment-card-number-1') : null);
        const $cvc = $blockEl.querySelector<HTMLElement>('.card-iframe-mount.payment-cvc-input') ??
            (blockId === 'payment-card-block-1' ? document.getElementById('payment-cvc-1') : null);
        if (!$number || !$cvc || !$number.id || !$cvc.id) {
            throw new Error(`mountCardBlock: missing iframe mount elements for ${blockId}`);
        }
        // PAN/CVC live in cross-origin BlockChyp iframes, so HTML5 can't
        // validate them natively. We deliberately do NOT gate form submit on
        // the iframe's self-reported state: BlockChyp's `cardType`/`validate`
        // messages are not a submit-time validity proxy. Instead, PAN/CVC are
        // enforced at charge time — chargePayment's tokenize step rejects an
        // empty/invalid PAN or CVC and surfaces that error. Same approach as
        // the express / depot / signature checkouts.
        const companyID = deps?.getCompanyID?.() ?? '';
        let webPaymentsToken: Promise<string>;
        if (companyID) {
            webPaymentsToken = GetRealm(companyID, { data: 'fattmerchantOmniWebPaymentsToken' }).then(realm => {
                const token = realm?.fattmerchantOmniWebPaymentsToken ?? '';
                if (!token) {
                    throw new Error('This realm has no Stax web-payments token configured.');
                }
                return token;
            });
        } else {
            webPaymentsToken = Promise.reject(new Error('Pick a customer before entering a card.'));
        }
        const mount = new blockchyp.Mount({
            webPaymentsToken,
            numberElID: $number.id,
            cvvElID: $cvc.id,
            numberWrapper: $number,
            cvvWrapper: $cvc,
            alternatePayments: blockchyp.OtherPaymentMethodNames($type),
            // IAP has dark mode + per-realm themes — neither the BS5 light
            // default nor the express Roboto/BS4 style works here. Compute
            // the iframe input's CSS from a live `.form-control` probe so
            // the iframe-rendered PAN/CVC matches whatever theme the admin
            // currently has on.
            fieldStyle: blockchyp.FormControlFieldStyle(),
        });
        cardMounts.set(blockId, mount);
        void mount.ready.catch(() => {
            mount.unload();
            // A realm change can recycle this block before a slow ready
            // rejection lands — only clear the registry slot if it still
            // holds THIS mount, or the delayed catch would orphan the
            // replacement (charge then fails with "no BlockChyp mount").
            if (cardMounts.get(blockId) === mount) {
                cardMounts.delete(blockId);
            }
        });
    }

    function unmountCardBlock($blockEl: HTMLElement) {
        const blockId = $blockEl.id;
        cardMounts.get(blockId)?.unload();
        cardMounts.delete(blockId);
    }

    function unmountAllCardBlocks() {
        for (const mount of cardMounts.values()) {
            mount.unload();
        }
        cardMounts.clear();
    }

    function mountAllCardBlocks() {
        for (const $block of getCardBlockElements()) {
            // Saved-card blocks have their iframe mounts hidden — no point
            // binding a cross-origin frame to a hidden div for a row that
            // won't tokenize.
            if (blockExistingID.has($block.id)) {
                continue;
            }
            mountCardBlock($block);
        }
    }

    // Rebuild any card block whose mount is missing or retired (a tokenize
    // timeout unloads its mount permanently — see ts/blockchyp.ts). Without
    // this, a decline caused by a timeout leaves the admin staring at dead
    // iframes with no way to re-enter the card.
    function remountRetiredCardBlocks() {
        for (const $block of getCardBlockElements()) {
            if (blockExistingID.has($block.id)) {
                continue;
            }
            const mount = cardMounts.get($block.id);
            if (!mount || mount.isUnloaded) {
                mountCardBlock($block);
            }
        }
    }

    // Unload + remount to wipe iframe contents. BlockChyp iframes are
    // cross-origin so we can't clear them in-place — tearing the frames
    // down and creating a fresh one is the only way to reset what the
    // admin typed. Called from clearForm + refresh so a typed-but-not-
    // saved card never leaks across resets.
    function recycleCardMounts() {
        unmountAllCardBlocks();
        syncCardMountsForType(selectedType());
    }

    // ─── Split UI ───────────────────────────────────────
    function getCardBlockElements(): HTMLElement[] {
        const blocks: HTMLElement[] = [document.getElementById('payment-card-block-1') as HTMLElement];
        $additionalCardBlocks.querySelectorAll<HTMLElement>('.payment-card-block').forEach($b => blocks.push($b));
        return blocks;
    }

    function getCardFields($blockEl: HTMLElement) {
        const isFirst = $blockEl.id === 'payment-card-block-1';
        return {
            $expMonth: isFirst ? $expMonth1 : $blockEl.querySelector('.payment-exp-month-select') as HTMLSelectElement,
            $expYear: isFirst ? $expYear1 : $blockEl.querySelector('.payment-exp-year-select') as HTMLSelectElement,
            $amount: isFirst ? $cardAmt1 : $blockEl.querySelector('.payment-card-amount-input') as HTMLInputElement,
        };
    }

    function addCardBlock() {
        nextCardBlockId++;
        const id = nextCardBlockId;
        const $el = template.Clone('#payment-card-block-template').firstElementChild as HTMLElement;
        $el.id = `payment-card-block-${id}`;

        populateYearSelect($el.querySelector('.payment-exp-year-select') as HTMLSelectElement);

        // BlockChyp iframe mount ids ride the same monotonic counter — once
        // a Mount captures these element ids at construction it never
        // sees them migrate, even if a middle block is removed before
        // initialization finishes.
        const $numberMount = $el.querySelector('.payment-card-number-input') as HTMLElement;
        const $cvcMount = $el.querySelector('.payment-cvc-input') as HTMLElement;
        $numberMount.id = `payment-card-number-${id}`;
        $cvcMount.id = `payment-cvc-${id}`;

        const $amt = $el.querySelector('.payment-card-amount-input') as HTMLInputElement;
        $amt.addEventListener('input', () => {
            updateSplitDisplay();
            notifyChange();
        });

        const $remove = $el.querySelector('.payment-card-remove-btn') as HTMLButtonElement;
        $remove.addEventListener('click', () => removeCardBlock($el));

        const $savedChange = $el.querySelector('.payment-card-block-saved-change') as HTMLButtonElement;
        $savedChange.addEventListener('click', () => clearBlockExisting($el));

        const $remaining = $el.querySelector('.payment-card-amount-remaining') as HTMLButtonElement;
        wireRemainingHelper($remaining, $amt);

        $additionalCardBlocks.appendChild($el);
        relabelCardBlocks();
        renderBlockOptions($el);
        mountCardBlock($el);
        // Refresh warning + helper labels every time a card is added so
        // the admin sees the projected total update immediately.
        updateSplitDisplay();
    }

    function removeCardBlock($blockEl: HTMLElement) {
        // Authoritative version of the disabled remove button: a block whose
        // charge POST threw may have committed — its idempotency key must
        // survive for the retry to replay (#5656).
        if (ambiguousChargeBlocks.has($blockEl.id)) {
            return;
        }
        unmountCardBlock($blockEl);
        blockExistingID.delete($blockEl.id);
        $blockEl.remove();
        relabelCardBlocks();
        updateSplitDisplay();
        notifyChange();
    }

    // Populate the per-block "Use a card on file" pill scroller from the
    // current options list. Hidden when the customer has no saved cards.
    // Called from addCardBlock (when the block is first added) and from
    // renderOptions (when options reload — e.g. customer switch).
    function renderBlockOptions($blockEl: HTMLElement) {
        const $row = $blockEl.querySelector('.payment-card-block-options') as HTMLElement;
        const $scroller = $blockEl.querySelector('.payment-card-block-options-scroller') as HTMLElement;
        if (options.length === 0) {
            $row.classList.add('d-none');
            $scroller.replaceChildren();
            return;
        }
        const f = document.createDocumentFragment();
        for (const po of options) {
            if (!po.id) {
                continue;
            }
            const $pill = template.Clone('#payment-card-block-option-pill-template').firstElementChild as HTMLElement;
            $pill.dataset.id = po.id;
            const brand = cardBrand(po.creditCardFirst6);
            ($pill.querySelector('.payment-card-block-option-pill-icon') as HTMLElement).className = `payment-card-block-option-pill-icon ${brand.faClass}`;
            ($pill.querySelector('.payment-card-block-option-pill-brand') as HTMLElement).textContent = brand.name;
            ($pill.querySelector('.payment-card-block-option-pill-last4') as HTMLElement).textContent = formatLast4(po);
            $pill.addEventListener('click', () => applyExistingOptionToBlock(po, $blockEl));
            f.appendChild($pill);
        }
        $scroller.replaceChildren(f);
        $row.classList.remove('d-none');
    }

    // Swap a block into saved-card mode. Drops the BlockChyp mount so the
    // cross-origin slot isn't held for a row that won't tokenize. The
    // amount field stays visible — admin picks how much to charge.
    function applyExistingOptionToBlock(po: models.HyejooPaymentOption, $blockEl: HTMLElement) {
        if (!po.id) {
            return;
        }
        blockExistingID.set($blockEl.id, po.id);
        unmountCardBlock($blockEl);
        fillSavedCardChip($blockEl, po);
        ($blockEl.querySelector('.payment-card-block-new') as HTMLElement).classList.add('d-none');
        ($blockEl.querySelector('.payment-card-block-saved') as HTMLElement).classList.remove('d-none');
        notifyChange();
    }

    function clearBlockExisting($blockEl: HTMLElement) {
        blockExistingID.delete($blockEl.id);
        ($blockEl.querySelector('.payment-card-block-saved') as HTMLElement).classList.add('d-none');
        ($blockEl.querySelector('.payment-card-block-new') as HTMLElement).classList.remove('d-none');
        if (selectedType() === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT && !$skip.checked) {
            mountCardBlock($blockEl);
        }
        notifyChange();
    }

    // Refresh "Card N" labels based on current DOM order. Element ids
    // (block id, number/cvc mount ids) are immutable for the life of a
    // mount and intentionally NOT touched here.
    function relabelCardBlocks() {
        const $blocks = $additionalCardBlocks.querySelectorAll<HTMLElement>('.payment-card-block');
        $blocks.forEach(($block, i) => {
            const $label = $block.querySelector('.payment-card-block-label') as HTMLElement | null;
            if ($label) {
                $label.textContent = i18n('cardN').replace('%s', String(i + 2));
            }
        });
    }

    // Informational warning — admin checkout allows partial payment, so
    // this never blocks submit. Bundled with updateRemainingHelpers since
    // both depend on the same (amounts vs total) inputs.
    function updateSplitDisplay() {
        const total = deps?.getOrderTotal?.();
        if (!total) {
            $splitWarning.classList.add('d-none');
            updateRemainingHelpers();
            return;
        }
        const charged = computePaid();
        if (charged.eq(total)) {
            $splitWarning.classList.add('d-none');
        } else {
            const text = i18n('willChargeOf').replace('%s', DollarFormat(charged)).replace('%s', DollarFormat(total));
            if ($splitWarning.textContent !== text) {
                $splitWarning.textContent = text;
            }
            $splitWarning.classList.remove('d-none');
        }
        updateRemainingHelpers();
    }

    // Refreshes the "Set to remaining ($X.XX)" helper button under each
    // amount input. Each button shows what THIS field would need to fill
    // to reach the total — total minus sum of OTHER fields' entered
    // values (blank treated as zero). Hidden when there's no remainder
    // or the order has no total yet.
    function updateRemainingHelpers() {
        const total = deps?.getOrderTotal?.();
        const $blocks = getCardBlockElements();
        const enteredAmounts: InstanceType<typeof Big>[] = $blocks.map($b => {
            const charged = chargedAmounts.get($b.id);
            if (charged !== undefined) {
                return charged;
            }
            const s = getCardFields($b).$amount.value.trim();
            if (!s) {
                return Big(0);
            }
            const v = parseFloat(s);
            return isNaN(v) || v <= 0 ? Big(0) : Big(v);
        });
        const sum = enteredAmounts.reduce((a, b) => a.plus(b), Big(0));
        $blocks.forEach(($b, i) => {
            const $btn = remainingHelperFor($b);
            if (!$btn) {
                return;
            }
            if (!total) {
                $btn.classList.add('d-none');
                return;
            }
            // Sum of other fields' entered values = sum - this field's value
            const others = sum.minus(enteredAmounts[i]);
            const remaining = total.minus(others);
            // Hide when there's no remaining to claim OR when clicking
            // would set the field to the value it already has.
            if (remaining.lte(0) || remaining.eq(enteredAmounts[i])) {
                $btn.classList.add('d-none');
                return;
            }
            const remainingStr = remaining.toFixed(2);
            if ($btn.dataset.remaining !== remainingStr) {
                $btn.textContent = i18n('setToRemaining').replace('%s', DollarFormat(remaining));
                $btn.dataset.remaining = remainingStr;
            }
            $btn.classList.remove('d-none');
        });
    }

    function remainingHelperFor($blockEl: HTMLElement): HTMLButtonElement | null {
        if ($blockEl.id === 'payment-card-block-1') {
            return document.getElementById('payment-card-amount-remaining-1') as HTMLButtonElement | null;
        }
        return $blockEl.querySelector<HTMLButtonElement>('.payment-card-amount-remaining');
    }

    // Wire a per-block helper button to fill its amount field with the
    // current remaining balance (computed by updateRemainingHelpers and
    // stashed on data-remaining when shown).
    function wireRemainingHelper($btn: HTMLButtonElement, $amount: HTMLInputElement) {
        $btn.addEventListener('click', () => {
            const v = $btn.dataset.remaining;
            if (!v) {
                return;
            }
            $amount.value = v;
            updateSplitDisplay();
            notifyChange();
        });
    }

    function clearBillingForm() {
        $billingName.value = '';
        $billingCompany.value = '';
        $billingAddress1.value = '';
        $billingAddress2.value = '';
        $billingCity.value = '';
        $billingZip.value = '';
        $billingEmail.value = '';
        // intlTelInput's setNumber is async — gated behind ready() the
        // same way shipping.ts clears its recipient phone.
        void billingPhone.ready().then(() => billingPhone.iti.setNumber(''));
        void billingCountriesReady.then(() => {
            $billingCountry.value = models.CountryID.COUNTRIES_UNITED_STATES.toString();
            $billingCountry.dispatchEvent(new Event('change'));
            $billingState.value = '';
            notifyChange();
        });
    }

    function clearForm() {
        $type.value = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT.toString();
        showFieldsetForType(selectedType());
        $sameAsShipping.checked = true;
        $billingFieldset.classList.add('d-none');
        $billingFieldset.disabled = true;
        clearBillingForm();
        clearActiveCard();
        revertBlockToNewCardMode($cardBlock1);
        $poNumber.value = '';
        $cardPoNumber.value = '';
        poUploader.reset();
        syncPoUploadValidity();
        // Unload mounts before wiping additional-block DOM so the SDK
        // isn't bound to detached nodes; don't reset nextCardBlockId —
        // the monotonic-id invariant must hold across resets.
        for (const $block of $additionalCardBlocks.querySelectorAll<HTMLElement>('.payment-card-block')) {
            unmountCardBlock($block);
        }
        $additionalCardBlocks.replaceChildren();
        blockExistingID.clear();
        // PAN/CVC live in the BlockChyp iframe and need a remount to clear.
        $expMonth1.selectedIndex = 0;
        $expYear1.selectedIndex = 0;
        $cardAmt1.value = '';
        recycleCardMounts();
        // Refresh warning + per-field helpers from the now-empty state —
        // otherwise card-1's "Set to remaining ($X)" button can retain a
        // stale value computed from extras that were just removed.
        updateSplitDisplay();
        notifyChange();
    }

    // Visual-only swap back to new-card mode for a block — drops chip,
    // shows form, clears the per-block existingID. Doesn't touch mounts
    // (caller decides whether to remount, depending on type/skip state).
    function revertBlockToNewCardMode($blockEl: HTMLElement) {
        blockExistingID.delete($blockEl.id);
        ($blockEl.querySelector('.payment-card-block-saved') as HTMLElement).classList.add('d-none');
        ($blockEl.querySelector('.payment-card-block-new') as HTMLElement).classList.remove('d-none');
    }

    // Top-scroller pick → routed to the same per-block apply that the
    // inline pills use. Adds the top-scroller-only bookkeeping (sticky
    // "active" highlight, force CC type).
    function applyExistingOption(po: models.HyejooPaymentOption, $card: HTMLElement) {
        if (topOptionsLocked) {
            return;
        }
        if (!po.id || po.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
            return;
        }
        clearActiveCard();
        $card.classList.add('active');
        $activeCard = $card;
        $type.value = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT.toString();
        showFieldsetForType(models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT);
        applyExistingOptionToBlock(po, $cardBlock1);
    }

    function fillSavedCardChip($blockEl: HTMLElement, po: models.HyejooPaymentOption) {
        const brand = cardBrand(po.creditCardFirst6);
        const $icon = $blockEl.querySelector('.payment-card-block-saved-icon') as HTMLElement;
        $icon.className = `payment-card-block-saved-icon ${brand.faClass} fs-3 text-primary`;
        ($blockEl.querySelector('.payment-card-block-saved-brand') as HTMLElement).textContent = brand.name;
        ($blockEl.querySelector('.payment-card-block-saved-last4') as HTMLElement).textContent = formatLast4(po);
        const parts: string[] = [];
        if (po.creditCardExpirationDate) {
            parts.push(`Exp ${po.creditCardExpirationDate}`);
        }
        const $detail = $blockEl.querySelector('.payment-card-block-saved-detail') as HTMLElement;
        const locality = po.address.toOneLineShort();
        if (locality) {
            parts.push(locality);
        }
        $detail.textContent = parts.join(' · ');
    }

    function updateOptionsRowVisibility() {
        // Scroller is credit-only — the API returns only cards now, and
        // PO/check/wire have no reusable stored payment to prefill. Also
        // gate on skip + emptiness.
        const show = !$skip.checked &&
            selectedType() === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT &&
            options.length > 0;
        $optionsRow.classList.toggle('d-none', !show);
    }

    function renderOptions() {
        if (options.length === 0) {
            $optionsScroller.replaceChildren();
            refreshAllBlockOptions();
            updateOptionsRowVisibility();
            return;
        }

        const f = document.createDocumentFragment();
        for (const po of options) {
            if (!po.id || !po.paymentOptionTypeID) {
                continue;
            }
            const $card = template.Clone('#payment-option-card-template').firstElementChild as HTMLElement;
            $card.dataset.id = po.id;

            const $icon = $card.querySelector('.payment-option-card-icon') as HTMLElement;
            const $title = $card.querySelector('.payment-option-card-title') as HTMLElement;
            const $detail = $card.querySelector('.payment-option-card-detail') as HTMLElement;
            const $address = $card.querySelector('.payment-option-card-address') as HTMLElement;
            const $meta = $card.querySelector('.payment-option-card-meta') as HTMLElement;
            const $defaultBadge = $card.querySelector('.payment-option-card-default-badge') as HTMLElement;

            const brand = cardBrand(po.creditCardFirst6);
            $icon.className = `payment-option-card-icon small ${brand.faClass}`;
            $title.textContent = brand.name;
            $detail.textContent = optionDetailLine(po);
            $address.textContent = po.address.toOneLineShort();
            $defaultBadge.classList.toggle('d-none', !po.default);
            RenderUsageMeta($meta, po.orders);

            $card.addEventListener('click', () => applyExistingOption(po, $card));
            $card.addEventListener('keydown', e => {
                if (e.target !== $card) {
                    return;
                }
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    applyExistingOption(po, $card);
                }
            });

            f.appendChild($card);
        }
        $optionsScroller.replaceChildren(f);
        refreshAllBlockOptions();
        updateOptionsRowVisibility();
    }

    function refreshAllBlockOptions() {
        for (const $block of $additionalCardBlocks.querySelectorAll<HTMLElement>('.payment-card-block')) {
            renderBlockOptions($block);
        }
    }

    let fetchSeq = 0;
    function fetchOptions(userID: string) {
        const mySeq = ++fetchSeq;
        options = [];
        renderOptions();
        if (!userID) {
            return;
        }
        // Filtered to credit cards with a Stax token attached. PO / check /
        // wire don't have stored "charge against this" payment methods —
        // they're per-order data entry, not reusable — so they have no
        // place in the prefill scroller. valid:true drops cards that
        // weren't tokenized (would always fail at charge time).
        void axios.get('/v2/payment-options', {
            params: {
                'q': `user:${userID} active:true type:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT} valid:true`,
                'data': 'id,paymentOptionTypeID,address,creditCardFirst6,creditCardLast4,' +
                    'creditCardExpirationDate,orders,lastOrdered,default',
                // default desc surfaces the user-picked default card first
                // (Label='Default'); orders desc tiebreaks by most-recent use
                // so the next-most-likely option follows.
                'sort': 'default desc,orders desc',
                'max': 50,
            },
        }).then(resp => {
            if (mySeq !== fetchSeq) {
                return;
            }
            options = models.HyejooPaymentOptionsResponse.fromJSON(resp.data).results;
            renderOptions();
        }).catch((err: unknown) => {
            if (mySeq !== fetchSeq) {
                return;
            }
            ShowError(err);
        });
    }

    function syncCardMountsForType(type: models.PaymentOptionTypeID) {
        if (type === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT && !$skip.checked) {
            mountAllCardBlocks();
        } else {
            // Switching away from CC, or toggling skip on — drop iframes so
            // we're not leaving cross-origin frames bound to detached
            // (well, hidden) nodes. Re-mount on next CC entry.
            unmountAllCardBlocks();
        }
    }

    $type.addEventListener('change', () => {
        // Switching type clears card-1's saved-card pick — a stored credit
        // card is unrelated to the PO/check/wire the admin just selected.
        clearActiveCard();
        revertBlockToNewCardMode($cardBlock1);
        const type = selectedType();
        showFieldsetForType(type);
        syncCardMountsForType(type);
        updateOptionsRowVisibility();
        notifyChange();
    });

    // Card-1's "Use different" lives in the static block markup and isn't
    // wired by addCardBlock (which only runs for cloned blocks). Wire it
    // here; the handler is the same as additional blocks (clear the
    // saved-card pick, restore the new-card form + iframes) plus clearing
    // the top-scroller "active" highlight that the top-scroller flow sets.
    ($cardBlock1.querySelector('.payment-card-block-saved-change') as HTMLButtonElement).addEventListener('click', () => {
        clearActiveCard();
        clearBlockExisting($cardBlock1);
    });

    $splitAdd.addEventListener('click', () => {
        // addCardBlock updates the warning itself, no need to repeat.
        addCardBlock();
        notifyChange();
    });
    for (const $el of [$expMonth1, $expYear1]) {
        $el.addEventListener('change', () => notifyChange());
    }
    $cardAmt1.addEventListener('input', () => {
        updateSplitDisplay();
        notifyChange();
    });
    wireRemainingHelper(
        document.getElementById('payment-card-amount-remaining-1') as HTMLButtonElement,
        $cardAmt1,
    );

    $poNumber.addEventListener('input', () => notifyChange());
    $cardPoNumber.addEventListener('input', () => notifyChange());

    $sameAsShipping.addEventListener('change', () => {
        const hide = $sameAsShipping.checked;
        $billingFieldset.classList.toggle('d-none', hide);
        // Disabled tracks d-none so the hidden billing inputs' `required`
        // attrs don't trip ReportValidity at place-order time.
        $billingFieldset.disabled = hide;
        notifyChange();
    });

    // Billing form input nudges notifyChange so onPaymentChange picks up the
    // edit. Don't clear the existing-option pick on every keystroke — admins
    // routinely tweak a single billing field after picking a card.
    for (const $el of [$billingName, $billingCompany, $billingAddress1, $billingAddress2, $billingCity, $billingZip, $billingPhone, $billingEmail]) {
        $el.addEventListener('input', () => notifyChange());
    }
    for (const $el of [$billingCountry, $billingState]) {
        $el.addEventListener('change', () => notifyChange());
    }

    $clearBtn.addEventListener('click', () => clearForm());

    const collapse = bootstrap.Collapse.getOrCreateInstance($collapse, { toggle: false });
    $skip.addEventListener('change', () => {
        $fieldset.disabled = $skip.checked;
        if ($skip.checked) {
            collapse.hide();
        } else {
            collapse.show();
        }
        updateOptionsRowVisibility();
        syncCardMountsForType(selectedType());
        notifyChange();
    });

    showFieldsetForType(selectedType());
    // Default type is CC and the section is visible by default, so prime
    // the BlockChyp mounts now. Skip-toggle / type-switch handlers take it
    // from here.
    syncCardMountsForType(selectedType());

    // ─── Charge ─────────────────────────────────────────
    // Payments happen AFTER the order exists. chargePayment runs the selected
    // method against the new order: credit cards one at a time (decline→fix→
    // resume), a single manual (PO/check/wire) charge, or nothing on skip.
    function chargePayment(chargeDeps: ChargePaymentDeps): void {
        if ($skip.checked) {
            chargeDeps.onComplete();
            return;
        }
        const type = selectedType();
        if (type === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
            chargedAmounts.clear();
            chargeWebPaymentsToken = undefined;
            void chargeCreditBlocks(chargeDeps);
            return;
        }
        void chargeManual(type, chargeDeps);
    }

    // Resolve each block's charge amount, applying card-1's "blank means the
    // remainder" rule. An already-charged block (after a partial charge)
    // contributes its actual charged amount — never a recomputed remainder —
    // so adding/editing cards on retry can't silently re-bill it. Uncharged
    // extras must carry explicit positive amounts; an uncharged blank card-1
    // absorbs whatever's left of the order total. Sum-vs-total mismatch is
    // allowed (admin may intentionally undercollect) — only blank/zero/negative
    // individual amounts throw, since charging $0 to a card is never intended.
    function resolveSplitAmounts($blocks: HTMLElement[]): InstanceType<typeof Big>[] {
        const total = deps?.getOrderTotal?.() ?? Big(0);
        const amounts = new Array<InstanceType<typeof Big>>($blocks.length);
        // Sum of every block except a blank, uncharged card-1: charged blocks
        // their real charged amount, explicit cards their typed value. The
        // blank card-1 (if any) absorbs total minus this.
        let fixedSum = Big(0);
        let card1NeedsRemainder = false;
        for (let i = 0; i < $blocks.length; i++) {
            const charged = chargedAmounts.get($blocks[i].id);
            if (charged !== undefined) {
                amounts[i] = charged;
                fixedSum = fixedSum.plus(charged);
                continue;
            }
            const s = getCardFields($blocks[i]).$amount.value.trim();
            if (i === 0 && !s) {
                card1NeedsRemainder = true;
                continue;
            }
            if (!s) {
                throw new Error(`Card ${i + 1}: enter an amount or remove the card.`);
            }
            const v = Big(s);
            if (v.lte(0)) {
                throw new Error(`Card ${i + 1}: amount must be greater than zero.`);
            }
            amounts[i] = v;
            fixedSum = fixedSum.plus(v);
        }
        if (card1NeedsRemainder) {
            const remainder = total.minus(fixedSum);
            if (remainder.lte(0)) {
                // Other cards (already charged + entered) cover the full order
                // total — card-1 has nothing left to charge. Force the admin to
                // either drop a card or enter an explicit value.
                throw new Error('Card 1: enter an amount (other cards already cover the order total).');
            }
            amounts[0] = remainder;
        }
        return amounts;
    }

    // The billing address + ISO codes for a new-card charge. ISO codes come
    // from the live countries cache (the <select> options only carry data-*
    // codes on an explicit override; same-as-shipping leaves them undefined),
    // so resolving by ID handles both paths uniformly.
    function billingForCharge(): { address: models.Address; stateCode: string | undefined; countryCode3: string | undefined } {
        const address = currentBillingAddress();
        if (!address) {
            throw new Error('Billing address is required for credit card payment.');
        }
        if (!address.fullName) {
            // Cardholder name is the one address field tokenize ships straight
            // to BlockChyp; without it Stax declines later with a useless error.
            throw new Error('Cardholder name is required for credit card payment.');
        }
        const stateCode = address.stateID && address.countryID ?
            countries.GetStateSync(address.countryID, address.stateID)?.stateCode :
            undefined;
        const countryCode3 = address.countryID ?
            countries.GetCountrySync(address.countryID)?.countryCode3 :
            undefined;
        return { address, stateCode, countryCode3 };
    }

    // Charge every not-yet-charged card block, in DOM order. Blocks already in
    // chargedAmounts (charged earlier this run) are skipped and stay frozen.
    // On a decline (or a recoverable charge error) we stop, surface it on the
    // block, unlock everything not yet charged for correction, and hand the
    // place handler a retry() that re-charges the remaining cards — so the
    // admin can edit the declined card, split across a freshly added card, or
    // remove an unprocessed one before retrying.
    async function chargeCreditBlocks(chargeDeps: ChargePaymentDeps): Promise<void> {
        const $blocks = getCardBlockElements();

        // Account balance is charged first, and getOrderTotal() is already
        // post-balance — so when balance covers the whole order there's $0 left
        // to charge. A single default (blank-amount) card then has nothing to
        // charge: complete instead of erroring on the empty amount. An explicit
        // amount or extra cards mean the admin intends a card charge, so fall
        // through and let resolveSplitAmounts handle it.
        if ($blocks.length === 1 &&
            !chargedAmounts.has($blocks[0].id) &&
            !getCardFields($blocks[0]).$amount.value.trim() &&
            (deps?.getOrderTotal?.() ?? Big(0)).lte(0)) {
            chargeDeps.onComplete();
            return;
        }

        let amounts: InstanceType<typeof Big>[];
        try {
            amounts = resolveSplitAmounts($blocks);
        } catch (err) {
            // A bad split amount (blank/zero extra, over-covered card-1) is
            // fixable in place — route it through the decline path so the
            // uncharged cards unlock for correction, instead of onError, which
            // would leave the form locked and the admin stuck re-submitting the
            // same invalid amounts.
            handleCreditDecline(err instanceof Error ? err.message : i18n('enterValidAmountEachCard'), chargeDeps);
            return;
        }

        freezeCardIframes();
        const purchaseOrderNumber = $cardPoNumber.value.trim();

        for (let i = 0; i < $blocks.length; i++) {
            if (chargedAmounts.has($blocks[i].id)) {
                continue;
            }
            const result = await chargeOneBlock($blocks[i], amounts[i], i, purchaseOrderNumber, chargeDeps);
            if (result === 'declined') {
                return;
            }
        }

        chargeDeps.onComplete();
    }

    // Realm Stax web-payments token for the BlockChyp→Stax exchange. Fetched
    // lazily + cached for the charge run — an all-saved-card split never needs
    // it. chargePayment resets it at the start of each run.
    let chargeWebPaymentsToken: string | undefined;
    async function ensureWebPaymentsToken(companyID: string): Promise<string> {
        if (chargeWebPaymentsToken === undefined) {
            const realm = await GetRealm(companyID, { data: 'fattmerchantOmniWebPaymentsToken' });
            chargeWebPaymentsToken = realm?.fattmerchantOmniWebPaymentsToken ?? '';
        }
        return chargeWebPaymentsToken;
    }

    // Charge a single card block. Returns 'charged' on success (recorded in
    // chargedAmounts) or 'declined' on a soft decline / recoverable error
    // (already routed to the place handler via handleCreditDecline). displayIdx
    // is the block's DOM position, for the "Charging card N…" label.
    // purchaseOrderNumber is snapshotted once per charge attempt so every card
    // in a split records the same order reference.
    async function chargeOneBlock(
        $block: HTMLElement,
        amount: InstanceType<typeof Big>,
        displayIdx: number,
        purchaseOrderNumber: string,
        chargeDeps: ChargePaymentDeps,
    ): Promise<'charged' | 'declined'> {
        const $state = chargeStateEl($block);
        $state.className = 'payment-card-charge-state small mt-2 text-body-secondary';
        $state.innerHTML = `<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Charging card ${displayIdx + 1}…`;

        let last4 = '';
        let idemKey = blockIdemKeys.get($block.id);
        if (!idemKey) {
            idemKey = crypto.randomUUID();
            blockIdemKeys.set($block.id, idemKey);
        }
        try {
            const existing = blockExistingID.get($block.id);
            let result: models.OrderPaymentCreateResponse;
            if (existing) {
                ambiguousChargeBlocks.add($block.id);
                result = await ChargeOrderPayment({ orderID: chargeDeps.orderID, paymentOptionID: existing, total: amount, purchaseOrderNumber }, undefined, idemKey);
                ambiguousChargeBlocks.delete($block.id);
            } else {
                const mount = cardMounts.get($block.id);
                if (!mount) {
                    throw new Error(`chargeOneBlock: no BlockChyp mount for ${$block.id}`);
                }
                const fields = getCardFields($block);
                const billing = billingForCharge();
                const expMonth = parseInt(fields.$expMonth.value, 10);
                const expYear = parseInt(fields.$expYear.value, 10);
                const $number = $block.querySelector<HTMLElement>('.card-iframe-mount.payment-card-number-input') ??
                    ($block.id === 'payment-card-block-1' ? document.getElementById('payment-card-number-1') : null);
                const $cvc = $block.querySelector<HTMLElement>('.card-iframe-mount.payment-cvc-input') ??
                    ($block.id === 'payment-card-block-1' ? document.getElementById('payment-cvc-1') : null);
                // Clear any painted field error from a prior tokenize failure.
                for (const $wrapper of [$number, $cvc]) {
                    if (!$wrapper) {
                        continue;
                    }
                    $wrapper.classList.remove('is-invalid');
                    let $sibling = $wrapper.nextElementSibling;
                    if ($sibling?.classList.contains('blockchyp-validity-mirror')) {
                        $sibling = $sibling.nextElementSibling;
                    }
                    if ($sibling?.classList.contains('invalid-feedback')) {
                        $sibling.remove();
                    }
                }
                let tokenized: blockchyp.TokenizedCard;
                try {
                    tokenized = await mount.tokenize({
                        fullName: billing.address.fullName,
                        month: expMonth,
                        year: expYear,
                        addressLine1: billing.address.addressLine1,
                        addressLine2: billing.address.addressLine2,
                        city: billing.address.city,
                        stateCode: billing.stateCode,
                        zip: billing.address.postalCode,
                        countryCode3: billing.countryCode3,
                        company: billing.address.organization,
                    });
                } catch (err) {
                    if (err instanceof blockchyp.TokenizeFieldError) {
                        const $wrapper = err.field === 'number' ? $number : $cvc;
                        if ($wrapper) {
                            $wrapper.classList.add('is-invalid');
                            let $anchor: Element = $wrapper;
                            if ($anchor.nextElementSibling?.classList.contains('blockchyp-validity-mirror')) {
                                $anchor = $anchor.nextElementSibling;
                            }
                            let $message = $anchor.nextElementSibling;
                            if (!$message?.classList.contains('invalid-feedback')) {
                                $message = document.createElement('div');
                                $message.classList.add('invalid-feedback');
                                $anchor.parentElement?.insertBefore($message, $anchor.nextElementSibling);
                            }
                            $message.textContent = err.message;
                            $wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
                        }
                        throw err;
                    }
                    throw err;
                }
                last4 = tokenized.lastFour ?? '';
                // BlockChyp→Stax: exchange the single-use BlockChyp token for a
                // reusable Stax payment-method id under the order's realm. The
                // charge endpoint takes that id on the inline option.
                const methodID = await stax.GetPaymentToken({
                    webPaymentsToken: await ensureWebPaymentsToken(chargeDeps.companyID),
                    paymentToken: tokenized.token,
                    card: { expireMonth: expMonth, expireYear: expYear },
                    fullName: billing.address.fullName,
                    company: billing.address.organization,
                    phone: billing.address.phoneNumber?.phone ?? '',
                    addressLine1: billing.address.addressLine1,
                    addressLine2: billing.address.addressLine2,
                    city: billing.address.city,
                    stateCode: billing.stateCode,
                    countryCode3: billing.countryCode3 ?? '',
                    zip: billing.address.postalCode,
                });
                ambiguousChargeBlocks.add($block.id);
                result = await ChargeOrderPayment({
                    orderID: chargeDeps.orderID,
                    total: amount,
                    purchaseOrderNumber,
                    paymentOption: {
                        paymentOptionTypeID: models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT,
                        fattmerchantOmniPaymentMethodID: methodID,
                        creditCardFirst6: tokenized.firstSix || undefined,
                        creditCardLast4: tokenized.lastFour || undefined,
                        creditCardExpirationDate: `${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2).padStart(2, '0')}`,
                        fullName: billing.address.fullName,
                        company: billing.address.organization,
                        addressLine1: billing.address.addressLine1,
                        addressLine2: billing.address.addressLine2,
                        city: billing.address.city,
                        stateID: billing.address.stateID,
                        zip: billing.address.postalCode,
                        countryID: billing.address.countryID,
                        phone: billing.address.phoneNumber?.phone,
                        email: billing.address.emailAddress?.email,
                    },
                }, undefined, idemKey);
                ambiguousChargeBlocks.delete($block.id);
            }

            if (result.declined) {
                const message = result.declineMessage || 'Card declined.';
                showBlockDeclined($state, message, last4);
                handleCreditDecline(message, chargeDeps);
                return 'declined';
            }
            // A retry may replay a committed response after the admin edited
            // the amount (or swapped the card) while the original response was
            // lost. The persisted rows are authoritative; using the retry
            // input would make the remaining split math — or the displayed
            // card — lie about what was actually charged.
            const chargedAmount = result.orderPayments.reduce((total, orderPayment) => total.plus(orderPayment.total), Big(0));
            if (result.creditCardLast4) {
                last4 = result.creditCardLast4;
            }
            chargedAmounts.set($block.id, chargedAmount);
            showBlockCharged($state, chargedAmount, last4);
            if (!chargedAmount.eq(amount.round(2))) {
                // The admin changed this amount after an ambiguous response,
                // but the server replayed the original committed charge. Stop
                // before charging later cards from the stale pre-replay split;
                // leave this block frozen and let them reconcile the rest.
                // Correct the block's input to what was actually taken first —
                // otherwise the split summary keeps validating against the
                // edited value and the retry can over/under-charge the rest.
                getCardFields($block).$amount.value = chargedAmount.toFixed(2);
                updateSplitDisplay();
                notifyChange();
                handleCreditDecline(i18n('chargeReplayMismatch').replace('%s', DollarFormat(chargedAmount)), chargeDeps);
                return 'declined';
            }
            return 'charged';
        } catch (err) {
            // Already painted on the offending card mount — no dialog on top
            // of it. Clear the "Charging card N…" spinner this function set
            // above, or it spins forever next to the painted field.
            if (err instanceof blockchyp.TokenizeFieldError) {
                $state.className = 'payment-card-charge-state small mt-2 d-none';
                $state.innerHTML = '';
                handleCreditDecline(err.message, chargeDeps);
                return 'declined';
            }
            // The order already exists, so a thrown failure here (tokenize, the
            // Stax exchange, a network blip — declines are 200, never thrown) is
            // still recoverable: surface the detail, then route through the same
            // decline path so the admin can fix/split and retry. A lost
            // ChargeOrderPayment response is safe to retry: this block's
            // idempotency key is reused and the server replays a committed
            // charge (#5656).
            ShowError(err);
            const message = i18n('couldntChargeCard');
            showBlockDeclined($state, message, last4);
            handleCreditDecline(message, chargeDeps);
            return 'declined';
        }
    }

    // After a card declines, unlock everything not yet charged — the declined
    // card's fields + iframe, any unreached cards, the "Add card" button, the
    // saved-card pickers — so the admin can correct it, split across a freshly
    // added card, or remove an unprocessed one. Charged cards stay frozen.
    // retry() re-locks and re-charges whatever's still unprocessed.
    function handleCreditDecline(message: string, chargeDeps: ChargePaymentDeps): void {
        remountRetiredCardBlocks();
        setUnchargedCardsLocked(false);
        chargeDeps.onDecline({
            message,
            retry: () => {
                setUnchargedCardsLocked(true);
                void chargeCreditBlocks(chargeDeps);
            },
        });
    }

    // Single manual charge (PO / check / wire). Not a gateway charge, so no
    // decline path — the server records a pending placeholder (its total is
    // asserted later by someone with the perm). A thrown failure is a hard
    // error the place handler surfaces.
    async function chargeManual(type: models.PaymentOptionTypeID, chargeDeps: ChargePaymentDeps): Promise<void> {
        try {
            const billing = currentBillingAddress();
            const isPO = type === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO;
            let idemKey = manualIdemKeys.get(type);
            if (!idemKey) {
                idemKey = crypto.randomUUID();
                manualIdemKeys.set(type, idemKey);
            }
            await ChargeOrderPayment({
                orderID: chargeDeps.orderID,
                // omitempty: '' is dropped on the wire for non-PO types; the
                // server only reads it for PO anyway.
                purchaseOrderNumber: isPO ? $poNumber.value.trim() : '',
                paymentOption: {
                    paymentOptionTypeID: type,
                    fullName: billing?.fullName,
                    company: billing?.organization,
                    addressLine1: billing?.addressLine1,
                    addressLine2: billing?.addressLine2,
                    city: billing?.city,
                    stateID: billing?.stateID,
                    zip: billing?.postalCode,
                    countryID: billing?.countryID,
                    phone: billing?.phoneNumber?.phone,
                    email: billing?.emailAddress?.email,
                },
            }, isPO ? await poUploader.uploadIDs() : undefined, idemKey);
            chargeDeps.onComplete();
        } catch (err) {
            chargeDeps.onError(err);
        }
    }

    // ─── Charge UI helpers ──────────────────────────────
    function chargeStateEl($blockEl: HTMLElement): HTMLElement {
        if ($blockEl.id === 'payment-card-block-1') {
            return document.getElementById('payment-card-charge-state-1') as HTMLElement;
        }
        return $blockEl.querySelector('.payment-card-charge-state') as HTMLElement;
    }

    function showBlockCharged($state: HTMLElement, amount: InstanceType<typeof Big>, last4: string): void {
        $state.className = 'payment-card-charge-state small mt-2 text-success';
        $state.textContent = i18n('chargedAmount').replace('%s', DollarFormat(amount)) + (last4 ? ` — ••••${last4}` : '');
    }

    function showBlockDeclined($state: HTMLElement, message: string, last4: string): void {
        $state.className = 'payment-card-charge-state small mt-2 text-danger';
        $state.textContent = `✗ ${message}${last4 ? ` — card ending ••••${last4}` : ''}`;
    }

    // BlockChyp iframes are cross-origin — a native fieldset `disabled` can't
    // reach them. pointer-events:none on the wrapper blocks focus/clicks so the
    // admin can't edit a card mid-charge (which would desync the next charge
    // from what's displayed). Pass specific blocks to freeze only those.
    function freezeCardIframes($blocks?: HTMLElement[]): void {
        for (const $block of $blocks ?? getCardBlockElements()) {
            $block.querySelectorAll<HTMLElement>('.card-iframe-mount').forEach($el => {
                $el.style.pointerEvents = 'none';
                $el.style.opacity = '0.6';
                $el.querySelectorAll('iframe').forEach($iframe => $iframe.setAttribute('tabindex', '-1'));
            });
        }
    }

    function thawCardIframes($blocks?: HTMLElement[]): void {
        for (const $block of $blocks ?? getCardBlockElements()) {
            $block.querySelectorAll<HTMLElement>('.card-iframe-mount').forEach($el => {
                $el.style.pointerEvents = '';
                $el.style.opacity = '';
                $el.querySelectorAll('iframe').forEach($iframe => $iframe.removeAttribute('tabindex'));
            });
        }
    }

    function setTopOptionsLocked(locked: boolean): void {
        topOptionsLocked = locked;
        $optionsScroller.style.pointerEvents = locked ? 'none' : '';
        $optionsScroller.style.opacity = locked ? '0.6' : '';
    }

    // Lock (or unlock) every card block that hasn't successfully charged this
    // run — its exp/amount fields, its iframe, its remove / use-different /
    // saved-card-pill buttons — plus the "Add card" button and the top
    // saved-card scroller. Charged blocks are skipped so they stay frozen +
    // disabled. Drives the decline-paused state (unlocked) and the re-lock when
    // a retry starts charging.
    function setUnchargedCardsLocked(locked: boolean): void {
        for (const $block of getCardBlockElements()) {
            if (chargedAmounts.has($block.id)) {
                continue;
            }
            const fields = getCardFields($block);
            fields.$expMonth.disabled = locked;
            fields.$expYear.disabled = locked;
            fields.$amount.disabled = locked;
            $block.querySelectorAll<HTMLButtonElement>(
                '.payment-card-remove-btn, .payment-card-block-saved-change, .payment-card-block-option-pill',
            ).forEach($btn => {
                // An ambiguous block (charge POST threw — may have committed)
                // stays non-removable: removing it abandons its idempotency
                // key and a replacement would charge on top (#5656).
                if (!locked && $btn.classList.contains('payment-card-remove-btn') && ambiguousChargeBlocks.has($block.id)) {
                    return;
                }
                $btn.disabled = locked;
            });
            if (locked) {
                freezeCardIframes([$block]);
            } else {
                thawCardIframes([$block]);
            }
        }
        $splitAdd.disabled = locked;
        // Once any card has charged — or a charge POST threw and may have
        // committed — the PO number stays locked: a retry replays the
        // committed charge with the PO it was sent, so an edit here would let
        // later cards in the split record a different one.
        $cardPoNumber.disabled = locked || chargedAmounts.size > 0 || ambiguousChargeBlocks.size > 0;
        // The top saved-card scroller (card-1 picker) is divs, not form
        // controls, so gate it with pointer-events like the iframe freeze. It
        // only ever targets card-1, so keep it locked once card-1 has charged —
        // otherwise the admin could swap the displayed card on an already-
        // charged block, showing a card different from the one billed.
        const card1Locked = locked || chargedAmounts.has($cardBlock1.id);
        setTopOptionsLocked(card1Locked);

        // Billing applies to every new-card charge, so unlock it on a decline
        // too: a card can fail purely because billing is missing — e.g.
        // same-as-shipping is on but shipping was skipped/incomplete, so
        // there's no address to inherit — and the admin must be able to uncheck
        // same-as-shipping and enter it. On re-lock the same-as-shipping gating
        // (fieldset disabled while the box is checked) is restored.
        $sameAsShipping.disabled = locked;
        $billingFieldset.disabled = locked || $sameAsShipping.checked;
        $billingFieldset.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>(
            'input, select, textarea, button',
        ).forEach($el => {
            $el.disabled = locked;
        });
    }

    return {
        refresh: userID => {
            clearActiveCard();
            // Switching customers — saved-card picks reference the old
            // customer, and any typed-but-unsaved PAN must not ride along
            // either. BlockChyp iframes are cross-origin so they can't be
            // cleared in place; recycle them.
            revertBlockToNewCardMode($cardBlock1);
            for (const $block of $additionalCardBlocks.querySelectorAll<HTMLElement>('.payment-card-block')) {
                unmountCardBlock($block);
            }
            $additionalCardBlocks.replaceChildren();
            blockExistingID.clear();
            updateSplitDisplay();
            recycleCardMounts();
            notifyChange();
            fetchOptions(userID);
        },
        refreshRealm: () => {
            // Saved-card picks stay valid (same customer) — only the live
            // iframes carry the realm-scoped key, so recycle just those.
            recycleCardMounts();
        },
        setShippingDestination: addrInit => {
            shippingAddr = addrInit;
            // Only re-notify if same-as-shipping is on — when off, the
            // billing form is the source of truth and the shipping change
            // doesn't affect anything we'd report.
            if ($sameAsShipping.checked) {
                notifyChange();
            }
        },
        refreshWarning: () => {
            // Total moved (shipping or tax-exempt change). Refresh the
            // inline warning + per-field helper labels and re-emit the
            // draft so summary picks up the new paid amount (card-1 blank
            // resolves against the new total).
            updateSplitDisplay();
            notifyChange();
        },
        freezeIframes: () => {
            freezeCardIframes();
            setTopOptionsLocked(true);
        },
        thawIframes: () => {
            thawCardIframes();
            setTopOptionsLocked(chargedAmounts.has($cardBlock1.id));
        },
        chargePayment,
        getPurchaseOrderNumbers: () => {
            if ($skip.checked) {
                return [];
            }
            const type = selectedType();
            let purchaseOrderNumber = '';
            if (type === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                purchaseOrderNumber = $poNumber.value.trim();
            } else if (type === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
                purchaseOrderNumber = $cardPoNumber.value.trim();
            }
            if (purchaseOrderNumber === '') {
                return [];
            }
            return [purchaseOrderNumber];
        },
    };
}

// Maps the brand name returned by cc.Issuer to the FA brand-icon class.
// Issuer covers Visa / Mastercard (incl. 2221-2720) / Amex / Discover /
// Diners / JCB / Maestro from BIN, so we just need the icon mapping. Any
// unrecognized BIN (or missing first6) falls back to a generic credit-
// card icon.
const cardBrandIcons: Record<string, string> = {
    'Visa': 'fab fa-cc-visa',
    'Mastercard': 'fab fa-cc-mastercard',
    'Amex': 'fab fa-cc-amex',
    'Discover': 'fab fa-cc-discover',
    'Diners Club': 'fab fa-cc-diners-club',
    'JCB': 'fab fa-cc-jcb',
};

// creditCardLast4 arrives already zero-padded. `!== undefined` so a literal
// "0000" renders correctly (Luhn-valid card numbers can end in 0000) — we only
// fall back to placeholder when the field is actually absent.
function formatLast4(po: models.HyejooPaymentOption, missing = '----'): string {
    return po.creditCardLast4 !== undefined ? po.creditCardLast4 : missing;
}

function cardBrand(firstSix: string | undefined): { name: string; faClass: string } {
    if (!firstSix) {
        return { name: 'Card', faClass: 'far fa-credit-card' };
    }
    const name = cc.Issuer(firstSix);
    const faClass = cardBrandIcons[name];
    if (!faClass) {
        return { name: 'Card', faClass: 'far fa-credit-card' };
    }
    return { name, faClass };
}

// Populates a year select with the next 16 years starting from the
// current calendar year. 16 is what express/depot use; long enough to
// cover any plausible card expiration.
function populateYearSelect($select: HTMLSelectElement) {
    const start = new Date().getFullYear();
    const f = document.createDocumentFragment();
    for (let i = 0; i < 16; i++) {
        const y = start + i;
        const $opt = document.createElement('option');
        $opt.value = y.toString();
        $opt.textContent = y.toString();
        f.appendChild($opt);
    }
    $select.replaceChildren(f);
}

// One-line summary for a card in the prefill scroller: "····1234 · exp 12/27".
// The scroller is credit-only now (queries filter to type=credit valid=true)
// so this never sees PO/check/wire — falls back to label only as a safety net
// in case the API ever returns a card row missing first6/last4.
function optionDetailLine(po: models.HyejooPaymentOption): string {
    const last4 = formatLast4(po, '');
    const exp = po.creditCardExpirationDate ? ` · exp ${po.creditCardExpirationDate}` : '';
    return (last4 ? `····${last4}` : '') + exp || (po.label ?? '');
}
